import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Camera, Check, Loader2, RotateCcw, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, API } from "../lib/api";
import { toast } from "sonner";

const SEVERITIES = [
  { key: "low", label: "Low", cls: "bg-gray-100 text-gray-700 border-gray-300" },
  { key: "medium", label: "Medium", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { key: "high", label: "High", cls: "bg-red-100 text-red-700 border-red-300" },
];

export const severityCls = (s) => (SEVERITIES.find((x) => x.key === s) || SEVERITIES[1]).cls;

const photoUrl = (p) => {
  const token = localStorage.getItem("auth_token");
  return `${API}/completion-photos/${p.storage_path}${token ? `?auth=${token}` : ""}`;
};

/** One issue row — same markup on the project, the pool card and the dispatch card. */
export const IssueRow = ({ issue, onChanged, compact = false }) => {
  const [busy, setBusy] = useState(false);
  const resolved = issue.status === "resolved";
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/issues/${issue.id}`, {
        status: resolved ? "open" : "resolved",
        resolved_by: resolved ? "" : (localStorage.getItem("fieldops-installer-name") || ""),
      });
      toast.success(resolved ? "Issue reopened" : "Issue resolved");
      onChanged && onChanged(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the issue");
    }
    setBusy(false);
  };
  return (
    <div
      className={`flex items-start gap-2 py-2 ${compact ? "" : "px-1"} border-b border-gray-100 last:border-0`}
      data-testid={`issue-row-${issue.id}`}
    >
      <span className={`text-[10px] uppercase font-bold border rounded px-1.5 py-0.5 shrink-0 mt-0.5 ${severityCls(issue.severity)}`}>
        {issue.severity}
      </span>
      <div className="min-w-0 flex-1">
        <div className={`text-sm font-medium ${resolved ? "text-gray-400 line-through" : "text-gray-900"}`}>
          {issue.title}
        </div>
        {issue.description && <div className="text-[12px] text-gray-600">{issue.description}</div>}
        <div className="text-[10px] text-gray-500 flex items-center gap-1.5 flex-wrap mt-0.5">
          <span className="font-mono text-purple-700">{issue.scope_type}</span>
          {issue.room_number && <span>Rm {issue.room_number}</span>}
          <span>{issue.author || "—"}</span>
          <span>{new Date(issue.created_at).toLocaleDateString()}</span>
          {resolved && <span className="text-emerald-700 font-semibold">resolved</span>}
        </div>
        {(issue.photos || []).length > 0 && (
          <div className="flex gap-1.5 mt-1">
            {issue.photos.map((p) => (
              <a key={p.id} href={photoUrl(p)} target="_blank" rel="noopener noreferrer" data-testid={`issue-photo-${p.id}`}>
                <img src={photoUrl(p)} alt={p.filename} className="w-12 h-12 object-cover rounded border border-gray-200" />
              </a>
            ))}
          </div>
        )}
      </div>
      <Button
        size="sm"
        variant={resolved ? "outline" : "default"}
        onClick={toggle}
        disabled={busy}
        className="h-8 shrink-0"
        data-testid={`issue-toggle-${issue.id}`}
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
          : resolved ? <><RotateCcw className="w-3.5 h-3.5 mr-1" />Reopen</>
            : <><Check className="w-3.5 h-3.5 mr-1" />Resolve</>}
      </Button>
    </div>
  );
};

/** Create-issue card, opened from a room × scope cell in the Install grid. */
export default function IssueDialog({ open, onOpenChange, projectId, scope, room, author, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) { setTitle(""); setDescription(""); setSeverity("medium"); setPhotos([]); }
  }, [open, scope, room?.key]);

  const attach = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    for (const f of files) {
      const fd = new FormData();
      fd.append("project_id", projectId);
      fd.append("uploader", author || "");
      fd.append("file", f);
      try {
        const { data } = await api.post("/completions/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((cur) => [...cur, data]);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not upload photo");
      }
    }
  };

  const save = async () => {
    if (!title.trim()) { toast.error("Give the issue a title"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/issues", {
        project_id: projectId,
        scope_type: scope,
        building: room.building || "",
        floor: room.floor || "",
        area: room.area || "",
        room_number: room.room_number || "",
        room_type: room.room_type || "",
        title, description, severity, photos, author: author || "",
      });
      toast.success("Issue created", { description: "Visible on the job and on any card holding this room" });
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the issue");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-md p-0 overflow-hidden" data-testid="issue-dialog">
        <DialogHeader className="px-4 py-3 border-b border-gray-200 bg-red-50">
          <DialogTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600" /> New issue
          </DialogTitle>
          <div className="text-[11px] text-gray-600">
            <span className="font-mono font-semibold text-purple-700">{scope}</span>
            {room?.room_number ? ` · Rm ${room.room_number}` : ""}
          </div>
        </DialogHeader>
        <div className="px-4 py-3 space-y-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="What's wrong?"
            className="w-full h-11 text-sm border border-gray-300 rounded-lg px-3"
            data-testid="issue-title-input"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Details for the PM / next crew…"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2"
            data-testid="issue-description-input"
          />
          <div className="flex items-center gap-2">
            {SEVERITIES.map((s) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setSeverity(s.key)}
                data-testid={`issue-severity-${s.key}`}
                className={`h-10 flex-1 rounded-lg border text-sm font-semibold ${severity === s.key ? s.cls : "bg-white text-gray-600 border-gray-300"}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple
                 onChange={attach} className="hidden" data-testid="issue-photo-input" />
          <Button type="button" variant="outline" className="w-full h-11"
                  onClick={() => fileRef.current?.click()} data-testid="issue-add-photo-btn">
            <Camera className="w-4 h-4 mr-2" /> Photo (optional)
          </Button>
          {photos.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {photos.map((p) => (
                <div key={p.id} className="relative">
                  <img src={photoUrl(p)} alt={p.filename} className="w-16 h-16 object-cover rounded border border-gray-200" />
                  <button type="button" onClick={() => setPhotos((cur) => cur.filter((x) => x.id !== p.id))}
                          className="absolute -top-1.5 -right-1.5 bg-white border border-gray-300 rounded-full p-0.5">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="px-4 py-3 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" className="h-11" onClick={() => onOpenChange(false)} data-testid="issue-cancel-btn">
            Cancel
          </Button>
          <Button className="h-11 bg-red-600 hover:bg-red-700 text-white" onClick={save} disabled={busy}
                  data-testid="issue-save-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
            Create issue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
